import { useState } from "react";
import { MessageSquare } from "lucide-react";
import { toast } from "react-toastify";
import { useStateValue } from "../../context/StateProvider";
import { actionType } from "../../context/reducer";
import {
  getCommentsForPost,
  addComment,
  updateComment,
  deleteComment,
} from "../../utils/firebasefunctions";
import CommentCard from "./CommentCard";
import CommentComposer from "./CommentComposer";
import CommentEditModal from "./CommentEditModal";
import { notifyMentions } from "../../utils/mentionNotify";
import { useActionDialog } from "../ActionDialog";

// Props:
//   post: parent post (we need post.id and post.commentCount for display)
function CommentsSection({ post }) {
  const [{ user, users, postComments }, dispatch] = useStateValue();
  const { confirm, dialog } = useActionDialog();
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [editingComment, setEditingComment] = useState(null);

  const comments = postComments[post.id];
  const commentCount = post.commentCount || 0;

  const handleExpand = async () => {
    if (expanded) {
      setExpanded(false);
      return;
    }
    setExpanded(true);
    if (comments !== undefined) return;
    setLoading(true);
    try {
      const fetched = await getCommentsForPost(post.id);
      dispatch({
        type: actionType.STORE_COMMENTS_FOR_POST,
        payload: { postId: post.id, comments: fetched },
      });
    } catch (err) {
      console.error("Failed to load comments:", err);
      toast.error("Failed to load comments");
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async (text) => {
    try {
      const newComment = await addComment(post.id, {
        authorId: user.id,
        authorName: `${user.firstname || ""} ${user.lastname || ""}`.trim(),
        authorRole: user.role || "",
        authorPhoto: user?.profilepicture || "",
        text,
      });
      dispatch({
        type: actionType.ADD_COMMENT,
        payload: { postId: post.id, comment: newComment },
      });
      // Notify anyone @-mentioned in this comment.
      await notifyMentions({
        text,
        users,
        sender: user,
        where: "a comment on the team feed",
        dealId: post.id,
      }).catch((err) => console.error("Mention notification failed:", err));
    } catch (err) {
      console.error("Failed to add comment:", err);
      toast.error("Failed to add comment");
      throw err;
    }
  };

  const handleEditSave = async (newText) => {
    if (!editingComment) return;
    try {
      await updateComment(post.id, editingComment.id, { text: newText });
      dispatch({
        type: actionType.UPDATE_COMMENT,
        payload: {
          postId: post.id,
          comment: {
            id: editingComment.id,
            text: newText,
            updatedAt: Date.now(),
          },
        },
      });
      setEditingComment(null);
    } catch (err) {
      console.error("Failed to edit comment:", err);
      toast.error("Failed to save");
      throw err;
    }
  };

  const handleDelete = async (comment) => {
    const ok = await confirm({
      title: "Delete comment",
      body: "Delete this comment?",
      tone: "danger",
      confirmLabel: "Delete",
    });
    if (!ok) return;
    try {
      await deleteComment(post.id, comment.id);
      dispatch({
        type: actionType.REMOVE_COMMENT,
        payload: { postId: post.id, commentId: comment.id },
      });
    } catch (err) {
      console.error("Failed to delete comment:", err);
      toast.error("Failed to delete");
    }
  };

  return (
    <div className="mt-3 pt-3 border-t border-gray-100">
      <button
        type="button"
        onClick={handleExpand}
        className="inline-flex items-center gap-1.5 text-[12px] text-gray-600 hover:text-slate-900"
      >
        <MessageSquare size={13} />
        {expanded
          ? "Hide comments"
          : commentCount === 0
            ? "Add a comment"
            : `View ${commentCount} comment${commentCount === 1 ? "" : "s"}`}
      </button>

      {expanded && (
        <div className="mt-2">
          {loading && comments === undefined && (
            <div className="text-[12px] text-gray-400 py-2">Loading…</div>
          )}
          {comments && comments.length > 0 && (
            <div className="divide-y divide-gray-100">
              {comments.map((c) => (
                <CommentCard
                  key={c.id}
                  comment={c}
                  currentUser={user}
                  onEdit={() => setEditingComment(c)}
                  onDelete={() => handleDelete(c)}
                />
              ))}
            </div>
          )}
          {comments !== undefined && (
            <CommentComposer currentUser={user} onSubmit={handleAdd} />
          )}
        </div>
      )}

      {editingComment && (
        <CommentEditModal
          comment={editingComment}
          onClose={() => setEditingComment(null)}
          onSave={handleEditSave}
        />
      )}
      {dialog}
    </div>
  );
}

export default CommentsSection;
